import { getCalendarDaysUntil, getMonthsUntil, isDateValid, type DateType } from '../../utils/dateUtils';

/**
 * Business rules for Pelecard CREDIT ("קרדיט") transactions — see
 * docs/pelecard/parameters.md for the wire-level `MinPayments` /
 * `MaxPayments` / `MinPaymentsForCredit` fields these feed, and
 * `initPelecardPaymentSession` in ./index.ts for where they're used.
 *
 * ## Three products, never combined
 *
 * A charge is a SINGLE payment, a CREDIT deal of
 * MIN_CREDIT_INSTALLMENTS..MAX_INSTALLMENTS (3..18, shekels, with interest), or
 * a dollar-linked SPLIT of MIN_SPLIT_INSTALLMENTS..calculateMaxPayments (2..18,
 * תשלומים, no interest — see the section at the end of this file). The 3-payment
 * floor is Pelecard's rule for CREDIT only; a תשלומים deal may be 2 payments.
 *
 * An interest-free split is sold ONLY dollar-linked. A plain shekel split would
 * clear — the 2026-09-18 probe got `CreditType=8`, `FixedPaymentTotal=2000` on
 * ₪100 over 5 — but it is not a product we offer.
 *
 * ## Credit is ILS-only
 *
 * Pelecard clears no multi-payment deal in any currency but shekels.
 * Verified 2026-09-18 against all four live Pegasus terminals (5745426012 /
 * 5745493013 / 5745101010 / 5745492011): `paymentsNumber` above 1 with
 * currency 2 ($), 4 (צמוד לדולר), 8 (צמוד מדד) or 978 (€) all return
 * `065 currency invalid` — the same code as a nonsense currency, and returned
 * before the card is even validated. Their manual states it outright:
 * "שימו לב: תשלומים ניתן לבצע בש"ח בלבד."
 *
 * Note what this does NOT mean: a USD *document* can still fund a credit deal,
 * it just has to reach the gateway as ILS. Callers converting a USD document
 * to ILS before charging should pass `isIls: true` — the flag describes the
 * WIRE currency, not the document's display currency.
 *
 * Mirrored — NOT imported, no shared module exists between this server and
 * the separate `tourRegistrationApp/` Vite project — in
 * `tourRegistrationApp/src/utils/pelecardInstallments.ts`, so the customer
 * wizard's selector agrees with what the server will actually accept.
 */

/** Never allow more than this many credit payments, regardless of how far
 *  away the trip is. */
export const MAX_INSTALLMENTS = 18;

/** The floor for a CREDIT deal — a fixed MIN_CREDIT_INSTALLMENTS..
 *  MAX_INSTALLMENTS range, independent of the trip date. Pelecard's own
 *  credit rules require at least 3 payments (and a ₪75 minimum — see
 *  docs/pelecard/error-codes.md, code 155: "סכום לתשלום בעסקת קרדיט קטן מידי
 *  (מינימום 75 ₪)"). Below this there is no deal at all, only a single
 *  payment. */
export const MIN_CREDIT_INSTALLMENTS = 3;

/**
 * Pelecard's own floor for a CREDIT deal, in SHEKELS. Below this the gateway
 * declines with `155 סכום לתשלום בעסקת קרדיט קטן מידי (מינימום 75 ₪)` — see
 * docs/pelecard/error-codes.md.
 *
 * A gateway constant, not a business rule: the tenant's own minimum deposit
 * (`tourregistrationsettings.minDepositAmount`, per passenger and quoted in the
 * tour's currency) is configurable and normally far above this — a $250 deposit
 * is roughly ₪760. This is only the backstop for a tenant who sets a very low
 * minimum, or none at all. Whichever is higher wins.
 *
 * In ILS because credit is ILS-only, so the charge amount is already shekels —
 * no conversion needed at the point of comparison.
 */
export const MIN_CREDIT_AMOUNT_ILS = 75;

/**
 * Default minimum per INSTALMENT, in shekels.
 *
 * Distinct from {@link MIN_CREDIT_AMOUNT_ILS}, which is Pelecard's documented
 * floor for the transaction as a whole ("סכום מינימום לביצוע עסקה 75 ₪"). A
 * per-payment floor is NOT in Pelecard's manual — it comes from the agreement
 * with the credit card company ("בהתאם להסכם עם חברת האשראי"), so it varies by
 * acquirer and terminal. Comparable Israeli checkouts publish figures around
 * ₪25 ("בין 3-36 תשלומים, תלוי בסכום העסקה המקורית, מינימום 25 ₪ לכל תשלום").
 *
 * Defaulted to the transaction floor until Pegasus's own number is confirmed —
 * deliberately conservative, since offering too FEW instalments only narrows
 * the choice, while offering too many produces a decline after the customer
 * has filled in the whole form. Override per terminal with
 * `paymentsterminals.minPaymentAmount`.
 */
export const MIN_CREDIT_PAYMENT_ILS = 75;


/** Fallback for `isSplitAllowed` when the tenant has no configured
 *  `fullPaymentWindowDays` (`tourregistrationsettings`) to hand it —
 *  matches that setting's own DB-row default. Below however many days this
 *  resolves to, a credit deal is not offered at all — the charge is forced
 *  to a single payment, both here and (for the UI) on the client. It is the
 *  SAME tenant-configurable number that forces full payment
 *  (`Step2ReviewPay`'s `lockedToFull`), so a client can never be offered
 *  credit up to the moment it is required to pay in full. */
export const SPLIT_MIN_DAYS_BEFORE_TRIP = 60;

/**
 * Whether a credit deal may be offered at all for this trip. False inside
 * `fullPaymentWindowDays` of the trip date — the charge must then be forced
 * to a single payment. Invalid/missing dates are treated as "not allowed"
 * (fail closed — a date we can't parse is not a trip we can safely offer a
 * payment plan against); see `tripDateApplies` for the one case where there
 * is legitimately no trip at all.
 *
 * `fullPaymentWindowDays` should be the CALLER's tenant's actual configured
 * value (`tourregistrationsettings.fullPaymentWindowDays`) — the same number
 * that forces full payment. Defaults to `SPLIT_MIN_DAYS_BEFORE_TRIP` only
 * for callers with no tenant context (e.g. a bare unit test).
 */
export const isSplitAllowed = (
    tripDate: DateType,
    fullPaymentWindowDays: number = SPLIT_MIN_DAYS_BEFORE_TRIP,
    today: DateType = new Date()
): boolean => {
    if (!isDateValid(tripDate) || !isDateValid(today)) return false;
    return getCalendarDaysUntil(tripDate, today) >= fullPaymentWindowDays;
};

export interface InstallmentPlanInput {
    /** Trip start date; missing/invalid is treated as "no credit allowed" —
     *  UNLESS `tripDateApplies` is false (see below). */
    tripDate: DateType;
    /**
     * False for a charge with no trip to gate against at all — a supplier/
     * agent payment (Financial Receipt/Remittance) or a non-tour service
     * travel file, where "N days before departure" has no meaning. Defaults
     * to true, which keeps every existing caller's fail-closed behaviour on
     * a missing/invalid date unchanged (a passenger's OWN trip missing its
     * date is a data problem, not "no trip", and must still block). Only
     * set false from a context that is CERTAIN there is no trip to gate
     * against — never to paper over a date that failed to load.
     */
    tripDateApplies?: boolean;
    /** True if the charge reaches Pelecard as ILS — credit is ILS-only, per
     *  the live-terminal probe documented above. This is the WIRE currency:
     *  a USD document converted to ILS before charging passes `true`. */
    isIls: boolean;
    /** True to run this as a CREDIT deal (with interest). The ONLY way to
     *  get more than a single payment. */
    isCreditDeal?: boolean;
    /** The credit-installment count the customer chose, when isCreditDeal
     *  is true. Clamped into the valid credit range — never trusted
     *  verbatim. */
    requestedCreditInstallments?: number;
    /** The tenant's configured `fullPaymentWindowDays` — see `isSplitAllowed`.
     *  Defaults to `SPLIT_MIN_DAYS_BEFORE_TRIP` when the caller has no
     *  tenant/client context to look one up for. */
    fullPaymentWindowDays?: number;
    today?: DateType;
}

export interface InstallmentPlanResult {
    /** The exact count to send as both Pelecard `MinPayments` and
     *  `MaxPayments` (pinning the count). Always 1 unless this resolved to a
     *  credit deal. */
    installmentCount: number;
    /** Pelecard `MinPaymentsForCredit`. `'0'` runs the charge as a single
     *  payment, never credit (Pelecard's manual: "אם מכניסים 0 או null -
     *  העסקה תתבצע כעסקת תשלומים בלבד"). */
    minPaymentsForCredit: string;
    /** True when the final plan is actually a credit deal — i.e.
     *  `isCreditDeal` was requested AND the ILS/window preconditions held. */
    isCreditDeal: boolean;
}

/** Sentinel MinPaymentsForCredit that guarantees a single-payment
 *  classification. Kept as a named constant — see the load-bearing comment
 *  history in ./index.ts on why this must never accidentally become a real
 *  threshold. */
const NEVER_CREDIT = '0';

/**
 * Resolves everything `initPelecardPaymentSession` needs to send for
 * `MinPayments` / `MaxPayments` / `MinPaymentsForCredit`, from the trip date
 * and the customer's requested plan. Single entry point so the ILS rule, the
 * full-payment-window gate and the 3..18 credit range all live in one place —
 * never trusts client input past this function.
 *
 * Anything that is not an allowed credit deal collapses to a single payment.
 * There is no third outcome.
 */
export const resolveInstallmentPlan = ({
    tripDate,
    isIls,
    isCreditDeal,
    requestedCreditInstallments,
    fullPaymentWindowDays = SPLIT_MIN_DAYS_BEFORE_TRIP,
    today = new Date(),
    tripDateApplies = true
}: InstallmentPlanInput): InstallmentPlanResult => {
    // No trip to gate against at all: skip the days-before-departure gate
    // rather than failing closed on a date that was never going to exist.
    const canSplit = isIls && (!tripDateApplies || isSplitAllowed(tripDate, fullPaymentWindowDays, today));

    if (!canSplit || !isCreditDeal) {
        return { installmentCount: 1, minPaymentsForCredit: NEVER_CREDIT, isCreditDeal: false };
    }

    // MinPaymentsForCredit must be <= the chosen count to classify the deal
    // as credit — pinning it to MIN_CREDIT_INSTALLMENTS here (never
    // NEVER_CREDIT) is what makes THIS request a credit deal.
    const installmentCount = Math.min(
        Math.max(Number(requestedCreditInstallments) || MIN_CREDIT_INSTALLMENTS, MIN_CREDIT_INSTALLMENTS),
        MAX_INSTALLMENTS
    );
    return {
        installmentCount,
        minPaymentsForCredit: String(MIN_CREDIT_INSTALLMENTS),
        isCreditDeal: true
    };
};

/**
 * Every payment count a charge may ask for, exactly as
 * {@link resolveInstallmentPlan} would honour it: `[1]` when no credit deal is
 * available, otherwise `[1, 3, 4, ... 18]`.
 *
 * Note what is NOT in that list: **2**. A count of 2 is neither a single
 * payment nor a valid credit deal, so it is never offered and never accepted.
 *
 * `fullPaymentWindowDays` must be the tenant's configured window (see
 * `isSplitAllowed`), or a selector built from this and the payment page
 * disagree for any tenant whose window is not the default.
 */
export const selectableInstallmentCounts = ({
    tripDate,
    isIls,
    fullPaymentWindowDays = SPLIT_MIN_DAYS_BEFORE_TRIP,
    today = new Date(),
    tripDateApplies = true
}: {
    tripDate: DateType;
    isIls: boolean;
    fullPaymentWindowDays?: number;
    today?: DateType;
    tripDateApplies?: boolean;
}): number[] => {
    const counts = [1];
    if (!isIls || (tripDateApplies && !isSplitAllowed(tripDate, fullPaymentWindowDays, today))) return counts;
    for (let n = MIN_CREDIT_INSTALLMENTS; n <= MAX_INSTALLMENTS; n += 1) counts.push(n);
    return counts;
};

/** The selectable CREDIT counts on their own — a fixed
 *  `[MIN_CREDIT_INSTALLMENTS .. MAX_INSTALLMENTS]` range, independent of the
 *  trip date (unlike whether credit is offered at all, which `isSplitAllowed`
 *  gates). */
export const creditInstallmentOptions = (): number[] => {
    const options: number[] = [];
    for (let n = MIN_CREDIT_INSTALLMENTS; n <= MAX_INSTALLMENTS; n += 1) options.push(n);
    return options;
};

export const CREDIT_INSTALLMENT_OPTIONS = creditInstallmentOptions();

/**
 * The credit counts worth offering for a given charge — those where each
 * instalment still clears the per-payment floor.
 *
 * Pelecard's documented rule is per TRANSACTION: "סכום מינימום לביצוע עסקה
 * 75 ₪ ו-3 תשלומים לפחות" (API manual, under DebitCreditType). By that rule
 * ₪770 over 18 is legal — ₪42.78 a month. We deliberately offer less than the
 * gateway allows, for two reasons:
 *
 *   · the same sentence says "בהתאם להסכם עם חברת האשראי" — the acquirer's own
 *     agreement sits on top of the documented floor and is not in any document
 *     we hold, so a per-payment minimum may exist that we cannot see. Being
 *     stricter can only turn a would-be decline into an option never shown;
 *     being laxer turns it into a decline after the customer has filled in the
 *     whole form.
 *   · ₪42 a month over 18 months is a poor product regardless of legality.
 *
 * `minPaymentAmount` defaults to the same ₪75 — conservative until the
 * acquirer confirms a real number, at which point it comes from the terminal.
 * Returns `[]` when the amount cannot support even the minimum count, which
 * callers should treat as "credit not available for this amount".
 */
export const creditInstallmentOptionsForAmount = (
    amountIls: number,
    options: { maxInstallments?: number | null; minPaymentAmount?: number | null } = {}
): number[] => {
    const cap = Math.min(options.maxInstallments || MAX_INSTALLMENTS, MAX_INSTALLMENTS);
    const floor = options.minPaymentAmount ?? MIN_CREDIT_PAYMENT_ILS;
    const counts: number[] = [];
    for (let n = MIN_CREDIT_INSTALLMENTS; n <= cap; n += 1) {
        if (floor > 0 && amountIls / n < floor) break;
        counts.push(n);
    }
    return counts;
};

// ── Dollar-linked split (תשלומים צמודי דולר) ─────────────────────────────────
//
// The one multi-payment product in DOLLARS. The customer sees and chooses $ on
// the payment page, which only checks the card (J2) and creates a token; the
// server then charges that token through DebitByIntInEmv as a SHEKEL deal whose
// instalments are linked to the dollar (creditTerms=8 תשלומים, currency=376,
// indexPayment=2). Pelecard links only a תשלומים deal — never credit — so the
// two products never combine: credit is ₪, the split is $.
//
// Offered only on a terminal approved for dollar linking
// (`paymentsterminals.allowsDollarLinked`). Mirrored — not imported — in
// tourRegistrationApp/src/utils/pelecardInstallments.ts.

/** StatusCode on a poll whose split is not settled yet — never '000', and
 *  never carrying a transaction id, so the client keeps polling. The poll also
 *  carries `linkedSplitStatus`: 'UNKNOWN' means the charge could not be
 *  confirmed and needs a person — never retried. See linkedSplit.ts. */
export const LINKED_SPLIT_PENDING_STATUS = 'LINKED_SPLIT_PENDING';

/** What staff are told when a dollar-linked charge cannot be confirmed. Their
 *  screens issue the receipt themselves, so "try again" would charge twice. */
export const LINKED_SPLIT_UNCONFIRMED_MESSAGE =
    'The installment charge could not be confirmed and the card may have been charged. ' +
    'Do not charge again: check Finance > Pelecard Transactions first.';

/** The floor for a dollar-linked split. Two is a valid תשלומים deal — the
 *  3-payment floor belongs to CREDIT (Pelecard's own rule), not to a split. */
export const MIN_SPLIT_INSTALLMENTS = 2;

/**
 * The most payments a split may have for this trip: two more than the whole
 * months left before it, never below 1 and never above MAX_INSTALLMENTS. A
 * split is paid off before the trip, so its ceiling shrinks as the trip nears —
 * unlike credit's fixed 3..18.
 */
export const calculateMaxPayments = (tripDate: DateType, today: DateType = new Date()): number =>
    Math.min(Math.max(getMonthsUntil(tripDate, today) + 2, 1), MAX_INSTALLMENTS);

/**
 * The split counts on offer for a charge of `amountIls` shekels: `[1]` alone
 * when none fits, otherwise `[1, 2, 3, ... cap]`. The cap is the smallest of
 * the trip's `maxPayments`, the terminal's `maxInstallments` and
 * MAX_INSTALLMENTS; every instalment must clear the per-payment floor, judged
 * in shekels because that is what is charged.
 */
export const linkedSplitOptionsForAmount = (
    amountIls: number,
    options: { maxPayments: number; maxInstallments?: number | null; minPaymentAmount?: number | null }
): number[] => {
    const cap = Math.min(options.maxPayments, options.maxInstallments || MAX_INSTALLMENTS, MAX_INSTALLMENTS);
    const floor = options.minPaymentAmount ?? MIN_CREDIT_PAYMENT_ILS;
    const counts = [1];
    for (let n = MIN_SPLIT_INSTALLMENTS; n <= cap; n += 1) {
        if (floor > 0 && amountIls / n < floor) break;
        counts.push(n);
    }
    return counts;
};

/**
 * Splits `amountAgorot` into DebitByIntInEmv's three fields so they always add
 * back up: `amount = firstPayment + notFirstPayment × noPayments`.
 *
 * Note `noPayments` counts the FIXED payments only — it does NOT include the
 * first one. Three payments in total is `noPayments = 2`. Any remainder from
 * the division goes on the first payment.
 */
export const splitLinkedInstalments = (
    amountAgorot: number,
    totalPayments: number
): { firstPayment: number; notFirstPayment: number; noPayments: number } => {
    const notFirstPayment = Math.floor(amountAgorot / totalPayments);
    const noPayments = totalPayments - 1;
    return { firstPayment: amountAgorot - notFirstPayment * noPayments, notFirstPayment, noPayments };
};
